import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../utils/api';

const REWARD_LABELS = { months_free: 'Months free', discount_pct: '% discount', credit_inr: 'INR credit' };

export default function ReferralConfig() {
  const [configs,  setConfigs]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({
    referralType: 'centre', name: '', isActive: true,
    rewardType: 'months_free', rewardValue: 1,
    validFrom: '', validUntil: '', requiresApproval: true,
  });

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/superadmin/referral-config');
      setConfigs(res.data.data.configs || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleToggle = async (id, isActive) => {
    try {
      await apiClient.patch(`/superadmin/referral-config/${id}`, { isActive: !isActive });
      fetchConfigs();
    } catch (e) {
      setError(e.response?.data?.message || 'Update failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this referral config?')) return;
    try {
      await apiClient.delete(`/superadmin/referral-config/${id}`);
      fetchConfigs();
    } catch (e) {
      setError(e.response?.data?.message || 'Delete failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await apiClient.post('/superadmin/referral-config', {
        ...form,
        rewardValue: Number(form.rewardValue),
        validFrom:   form.validFrom  || null,
        validUntil:  form.validUntil || null,
      });
      fetchConfigs();
      setForm({ referralType: 'centre', name: '', isActive: true, rewardType: 'months_free', rewardValue: 1, validFrom: '', validUntil: '', requiresApproval: true });
    } catch (e) {
      setError(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.wrap}>
      <h2 style={S.heading}>Referral Reward Config</h2>
      {error && <p style={S.error}>{error}</p>}

      {loading ? <p>Loading…</p> : (
        <table style={S.table}>
          <thead>
            <tr>{['Type', 'Name', 'Reward', 'Value', 'Active', ''].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {configs.length === 0 && <tr><td colSpan={6} style={S.td}>No configs yet.</td></tr>}
            {configs.map(c => (
              <tr key={c._id}>
                <td style={S.td}>{c.referralType}</td>
                <td style={S.td}>{c.name}</td>
                <td style={S.td}>{REWARD_LABELS[c.rewardType] || c.rewardType}</td>
                <td style={S.td}>{c.rewardValue}</td>
                <td style={S.td}>
                  <button onClick={() => handleToggle(c._id, c.isActive)} style={c.isActive ? S.btnGreen : S.btnGrey}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td style={S.td}>
                  <button onClick={() => handleDelete(c._id)} style={S.btnRed}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 style={S.subheading}>Add new config</h3>
      <form onSubmit={handleSubmit} style={S.form}>
        <label style={S.label}>Referral type
          <select value={form.referralType} onChange={e => setForm(f => ({ ...f, referralType: e.target.value }))} style={S.input}>
            <option value="centre">centre</option>
            <option value="solo">solo</option>
          </select>
        </label>
        <label style={S.label}>Config name
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={S.input} required />
        </label>
        <label style={S.label}>Reward type
          <select value={form.rewardType} onChange={e => setForm(f => ({ ...f, rewardType: e.target.value }))} style={S.input}>
            {Object.entries(REWARD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label style={S.label}>Reward value
          <input type="number" min="0" value={form.rewardValue} onChange={e => setForm(f => ({ ...f, rewardValue: e.target.value }))} style={S.input} required />
        </label>
        <label style={S.label}>Valid from (optional)
          <input type="date" value={form.validFrom} onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))} style={S.input} />
        </label>
        <label style={S.label}>Valid until (optional)
          <input type="date" value={form.validUntil} onChange={e => setForm(f => ({ ...f, validUntil: e.target.value }))} style={S.input} />
        </label>
        <button type="submit" disabled={saving} style={S.btnPrimary}>{saving ? 'Saving…' : 'Create config'}</button>
      </form>
    </div>
  );
}

const S = {
  wrap:       { padding: '24px', maxWidth: '860px' },
  heading:    { fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#1a3d2b' },
  subheading: { fontSize: '16px', fontWeight: '700', margin: '24px 0 12px', color: '#1a3d2b' },
  error:      { color: '#c0392b', marginBottom: '12px' },
  table:      { width: '100%', borderCollapse: 'collapse', marginBottom: '24px' },
  th:         { textAlign: 'left', padding: '8px 12px', background: '#f0f4f2', fontWeight: '600', fontSize: '13px', borderBottom: '1px solid #dde' },
  td:         { padding: '8px 12px', fontSize: '14px', borderBottom: '1px solid #eee' },
  form:       { display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '400px' },
  label:      { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', fontWeight: '600', color: '#374151' },
  input:      { padding: '8px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', minHeight: '36px' },
  btnPrimary: { background: '#1a6b5a', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 20px', fontWeight: '600', cursor: 'pointer', minHeight: '44px' },
  btnGreen:   { background: '#d4edda', color: '#1a6b5a', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' },
  btnGrey:    { background: '#e9ecef', color: '#555', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' },
  btnRed:     { background: '#f8d7da', color: '#721c24', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' },
};
